/*
 * Tiny promise-based IndexedDB wrapper (Block 2 B2.6). Three stores:
 *  - `kv`     : the cached guide snapshot, keyed by token → manifest/itinerary work OFFLINE.
 *  - `outbox` : queued mutations (check-in / incident / expense) written while offline; each
 *               carries a `clientId` (the keyPath) so the server can replay it idempotently.
 *  - `docs`   : supplier confirmation PDF blobs keyed by documentId (Block 4.5) — the guide SW
 *               deliberately ignores the cross-origin API, so document bytes are cached HERE
 *               (like the snapshot), fetched when online and viewed offline via an object URL.
 * No dependency (dexie etc.) — keeps the PWA bundle tiny.
 */

const DB_NAME = "bme-guide";
const DB_VERSION = 2;

export type OutboxKind = "checkin" | "incident" | "expense" | "photo";

export interface OutboxItem {
  clientId: string;
  kind: OutboxKind;
  payload: Record<string, unknown>;
  createdAt: number;
  label: string; // human summary for the pending-sync UI
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("kv")) db.createObjectStore("kv");
      if (!db.objectStoreNames.contains("outbox"))
        db.createObjectStore("outbox", { keyPath: "clientId" });
      if (!db.objectStoreNames.contains("docs")) db.createObjectStore("docs");
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export const kv = {
  get: <T>(key: string) => tx<T>("kv", "readonly", (s) => s.get(key) as IDBRequest<T>),
  set: (key: string, value: unknown) =>
    tx("kv", "readwrite", (s) => s.put(value, key) as IDBRequest<IDBValidKey>),
};

export const outbox = {
  add: (item: OutboxItem) =>
    tx("outbox", "readwrite", (s) => s.put(item) as IDBRequest<IDBValidKey>),
  all: () => tx<OutboxItem[]>("outbox", "readonly", (s) => s.getAll() as IDBRequest<OutboxItem[]>),
  remove: (clientId: string) =>
    tx("outbox", "readwrite", (s) => s.delete(clientId) as IDBRequest<undefined>),
};

/** Supplier confirmation PDF blobs (Block 4.5), keyed by documentId — cached online, viewed offline. */
export const docs = {
  get: (id: string) =>
    tx<Blob | undefined>("docs", "readonly", (s) => s.get(id) as IDBRequest<Blob | undefined>),
  set: (id: string, blob: Blob) =>
    tx("docs", "readwrite", (s) => s.put(blob, id) as IDBRequest<IDBValidKey>),
};

/** Cheap unique id for outbox idempotency keys (crypto.randomUUID where available). */
export function newClientId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `c_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e9).toString(36)}`;
}
